import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import {
  lessonPropType,
  chunkVisitPropType,
} from 'edrolo/domains/lessons/propTypes';
import { useEffect } from 'react';
import cx from 'classnames';
import { Icon, Pill, enums } from 'edrolo-ui';
import { useAnalytics } from 'edrolo/analytics';
import { usePlaybackRate } from './hooks';
import ShareLink from 'edrolo/components/common/ShareLink';
import urlResolver from 'edrolo/utils/URLResolver';
import { ActivityContainer } from 'edrolo/domains/activities';
import { StudentNote } from 'edrolo/domains/studentNotes';
import {
  ComingSoon,
  EmptyLesson,
  ToolbarSlideshow,
  LessonNav,
  TeachingGuide,
} from './components';
import style from './Lesson.scss';
import ChunkCarousel from '../ChunkCarousel';
import { useInjectable } from 'core/hooks';
import { useQuizAttempts } from 'edrolo/domains/lessons/hooks';
import InteractiveActivity from 'edrolo/domains/interactiveActivity/components/InteractiveActivity';
import { NAV_DESTINATION } from '../../constants';

/**
 * This is the core Lesson experience.
 * Please be aware that there are two contexts to consider when working on this component:
 *    The main application experience that is rendered in `edrolo/domains/lesson-chunkview/components/Chunkview`
 *    The Lesson Demo experience that is rendered in `edrolo/domains/lesson-chunkview/components/LessonDemo`
 *
 * Anything that happens in here down in the tree needs to be considered for
 * both authenticated and unauthenticated views.
 */

const Lesson = ({
  lesson,
  activeChunkIndex,
  replace,
  chunkVisits,
  recordChunkVisit,
  coursePageUrl,
  classId,
  bookmarks,
  buildChunkUrl,
  hasSuccessMeasure,
  showStudentNotes,
  studentNotebookUrl,
  onLessonComplete,
  isTeacherView,
  isStaff,
  nextLessonId,
  disableSharelinks = false,
  disableBookmarks = false,
  disableNotebook = false,
  hasQuestionSet,
}) => {
  const [playbackRate, setPlaybackRate] = usePlaybackRate();

  const activeChunk = lesson
    ?.get('chunks')
    ?.find((c) => c.get('index') === Number(activeChunkIndex));

  const { track } = useAnalytics({
    eventAttributes: {
      origin: 'chunkview_v2',
      class_id: classId,
      lesson_id: lesson.get('id'),
      chunk_id: activeChunk?.get('id'),
    },
  });

  const useQuizAttemptsInjectable = useInjectable(
    'useQuizAttemptsInjectable',
    useQuizAttempts
  );

  const {
    data: lessonQuizAttempts,
    loading: loadingQA,
    error: errorQA,
    save: saveQuizAttempt,
  } = useQuizAttemptsInjectable(lesson?.get('id'));

  useEffect(() => {
    //TODO: This could probably move to parent component (Chunkview or LessonDemo)
    if (!activeChunkIndex) {
      const firstChunk = lesson.get('chunks').first();
      const url = buildChunkUrl(firstChunk.get('index'));

      replace(url);
    }
  }, [activeChunkIndex, lesson, buildChunkUrl, replace]);

  useEffect(() => {
    if (activeChunk) {
      track('viewed chunk');
    }
  }, [activeChunk, track]);

  const successMeasureChunks = lesson
    ?.get('chunks')
    ?.filter((chunk) => chunk.get('success_measure') !== null);

  const nextChunk = lesson
    ?.get('chunks')
    ?.find((c) => c.get('index') === Number(activeChunkIndex) + 1);

  if (lesson?.get('chunks')?.size === 0) {
    return <EmptyLesson lesson={lesson} classId={classId} />;
  }

  if (lesson.get('is_coming_soon') && !isStaff) {
    return (
      <ComingSoon
        lesson={lesson}
        coursePageUrl={coursePageUrl}
        mySubjectsUrl={urlResolver.getPath('courses-page')}
      />
    );
  }

  const hasAttemptedQuestion =
    activeChunk &&
    lessonQuizAttempts.some(
      (qa) => qa.get('question') === activeChunk.getIn(['question', 'id'])
    );

  if (loadingQA || !activeChunk || !lesson?.get('id')) {
    return <div>Loading...</div>;
  }
  if (errorQA) {
    return <div>{errorQA}</div>;
  }

  return (
    <div className="lesson-v2">
      <div key={lesson.get('id')} className="lesson-body">
        <h1>
          {lesson.get('title')} &nbsp;
          {!disableSharelinks && (
            <ShareLink
              title="To share this lesson, copy and paste this link:"
              model="lesson"
              objectId={lesson.get('id')}
              classId={classId}
              shareType="lesson"
              origin="chunkview_v2"
            >
              <button className={cx('share-link', style.shareLink)}>
                <i className="icon-share-square-o" />
              </button>
            </ShareLink>
          )}
        </h1>
        <LessonNav
          buildChunkUrl={buildChunkUrl}
          chunks={lesson.get('chunks')}
          chunkVisits={chunkVisits}
        />
        {activeChunk.get('interactive_activity') ? (
          <>
            <InteractiveActivity
              key={activeChunk.get('id')}
              activityId={activeChunk.get('interactive_activity')}
            />
            {!nextChunk && (
              <div className={style.interactiveActivityNav}>
                {hasQuestionSet && (
                  <button
                    onClick={() => {
                      track('interactive activity nav clicked', {
                        destination: NAV_DESTINATION.QUESTION_SET,
                      });
                      onLessonComplete(NAV_DESTINATION.QUESTION_SET);
                    }}
                  >
                    <Pill
                      variant={enums.PillVariant.CIRCLE}
                      color={enums.ColorHue.GREEN}
                    >
                      <Icon name={enums.Icons.QUESTION} />
                    </Pill>
                    <span>Practice questions</span>
                    <Icon name={enums.Icons.CHEVRON_RIGHT} />
                  </button>
                )}
                <button
                  onClick={() => {
                    track('interactive activity nav clicked', {
                      destination: NAV_DESTINATION.NEXT_LESSON,
                    });
                    onLessonComplete(NAV_DESTINATION.NEXT_LESSON);
                  }}
                >
                  <Pill
                    variant={enums.PillVariant.CIRCLE}
                    color={enums.ColorHue.BLUE}
                  >
                    <Icon name={enums.Icons.LESSON} />
                  </Pill>
                  <span>Next lesson</span>
                  <Icon name={enums.Icons.CHEVRON_RIGHT} />
                </button>
              </div>
            )}
          </>
        ) : activeChunk.get('activity') ? (
          <ActivityContainer
            activityId={activeChunk.get('activity').toString()}
            nextUrl={nextChunk && buildChunkUrl(nextChunk.get('index'))}
            isTeacherView={isTeacherView}
            classId={classId}
            chunkId={activeChunk.get('id')}
            lessonId={lesson.get('id')}
            recordChunkVisit={recordChunkVisit}
          />
        ) : (
          <>
            <ChunkCarousel
              lesson={lesson}
              activeChunk={activeChunk}
              activeChunkIndex={parseInt(activeChunkIndex, 10)}
              playbackRate={playbackRate}
              nextLessonId={nextLessonId}
              onLessonComplete={onLessonComplete}
              recordChunkVisit={recordChunkVisit}
              buildChunkUrl={buildChunkUrl}
              hasSuccessMeasure={hasSuccessMeasure}
              successMeasureChunks={successMeasureChunks}
              lessonQuizAttempts={lessonQuizAttempts}
              saveQuizAttempt={saveQuizAttempt}
              hasAttemptedQuestion={hasAttemptedQuestion}
              hasQuestionSet={hasQuestionSet}
            />

            <ToolbarSlideshow
              key={`toolbar-${activeChunkIndex}`}
              lessonId={lesson.get('id')}
              chunkId={activeChunk.get('id')}
              classId={classId}
              bookmarks={bookmarks}
              playbackRate={playbackRate}
              onVideoSpeedChange={(speed) => setPlaybackRate(speed)}
              hasVideo={!!activeChunk.get('video')}
              disableSharelinks={disableSharelinks}
              disableBookmarks={disableBookmarks}
            />

            {showStudentNotes && !isTeacherView && (
              <StudentNote
                chunkId={activeChunk.get('id')}
                notebookUrl={studentNotebookUrl}
                disableNotebook={disableNotebook}
              />
            )}
          </>
        )}
        {(isTeacherView || isStaff) && <TeachingGuide chunk={activeChunk} />}
      </div>
    </div>
  );
};

Lesson.propTypes = {
  activeChunkIndex: PT.string, // TODO: number
  classId: PT.string, //TODO: number
  nextLessonId: PT.number,
  lesson: lessonPropType,
  chunkVisits: IPT.listOf(chunkVisitPropType),
  bookmarks: IPT.listOf(
    IPT.contains({
      reason: PT.string,
      comment: PT.string,
    })
  ),

  buildChunkUrl: PT.func,
  recordChunkVisit: PT.func,
  onLessonComplete: PT.func,
  replace: PT.func,

  hasSuccessMeasure: PT.bool,
  isStaff: PT.bool,
  isTeacherView: PT.bool,
  showStudentNotes: PT.bool,
  studentNotebookUrl: PT.string,
  coursePageUrl: PT.string,
  disableBookmarks: PT.bool,
  disableSharelinks: PT.bool,
  disableNotebook: PT.bool,
  hasQuestionSet: PT.bool,
};

Lesson.defaultProps = {
  onLessonComplete: () => {},
};

export default Lesson;
